import { camelCase, mapKeys, partial } from 'lodash';

import {
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
  EntityActions,
} from '@zapier/toolbox-redux-framework';
import { getJson, postJson } from '@zapier/toolbox-browser-fetch';

import { Options as MigrationOptions } from 'app/developer-v3/components/SidebarRight/Versioning/Migrate/Start';
import { Options as PromotionOptions } from 'app/developer-v3/components/SidebarRight/Versioning/Promote/Start';

import {
  IActions,
  IApi,
  ISelectors,
  LifecycleStage,
  LifecycleChangeType,
} from './types';

const doneStatuses = [
  LifecycleStage.aborted,
  LifecycleStage.complete,
  LifecycleStage.errored,
];

const pendingStatuses = [
  LifecycleStage.requested,
  LifecycleStage.estimating,
  LifecycleStage.inProgress,
];

export const type = 'versionLifecycle';

const API_BASE = '/api/platform/cli/apps';

export const api: IApi = buildEntityApi(
  type,
  API_BASE,
  (versionLifecycleApi: IApi) => {
    versionLifecycleApi.migrate = (appId, fromVersion, toVersion, options) =>
      postJson(`${API_BASE}/${appId}/migrations`, {
        job:
          'percentage' in options
            ? {
                from_version: fromVersion,
                name: 'migrate',
                percent_human: options.percentage,
                to_version: toVersion,
              }
            : {
                email: options.email,
                email_type: options.emailType,
                from_version: fromVersion,
                name: 'migrate',
                to_version: toVersion,
              },
      });

    versionLifecycleApi.promote = (appId, version, options) =>
      postJson(`${API_BASE}/${appId}/migrations`, {
        job: {
          app_metadata: options.appMetadata.map(
            ({ appChangeType, actionKey, actionType }) => ({
              app_change_type: appChangeType,
              action_key: actionKey,
              action_type: actionType,
            })
          ),
          changelog: options.changelog,
          is_bugfix: options.isBugFix,
          is_feature_update: options.isFeatureUpdate,
          is_other: options.isOther,
          loki_metadata: options.issueMetadata.map(
            ({ appChangeType, issueId }) => ({
              app_change_type: appChangeType,
              issue_id: issueId,
            })
          ),
          name: 'promote',
          to_version: version,
        },
      });

    versionLifecycleApi.loadProgress = appId =>
      getJson(`${API_BASE}/${appId}/migrations`);
  }
);

export const selectors: ISelectors = buildEntitySelectors(
  type,
  (sel: ISelectors) => {
    sel.id = partial(sel.dataItem, 'job_id');
    sel.appId = partial(sel.dataItem, 'app_id');
    sel.error = partial(sel.dataItem, 'error');
    sel.stage = partial(sel.dataItem, 'job_stage');
    sel.progress = entity =>
      sel.dataItem(['progress', 'overall_progress'], entity) || 0;
    sel.currentStep = entity => {
      const step = sel.dataItem(['progress', 'current_step'], entity);
      return mapKeys(step, (_, key) => camelCase(key));
    };
    sel.type = partial(sel.dataItem, 'job_kind');
    sel.isDone = entity => {
      const status = sel.stage(entity);
      return doneStatuses.includes(status);
    };
    sel.isPending = entity => pendingStatuses.includes(sel.stage(entity));
    sel.selectedApi = entity => ({
      from: sel.dataItem('from_selected_api', entity),
      to: sel.dataItem('to_selected_api', entity),
    });
    sel.steps = entity => {
      const steps = sel.dataItem(['progress', 'steps'], entity) || [];
      return steps.map(step => mapKeys(step, (_, key) => camelCase(key)));
    };
    sel.updatedAt = partial(sel.dataItem, 'updated_at');
    sel.all.findByAppId = partial(sel.all.findBy, (appId, entity) => {
      const parsedId = Number.isInteger(appId) ? appId : parseInt(appId, 10);
      return sel.appId(entity) === parsedId;
    });
  }
);

const createErroredLifecycle = (id: any, errorMessages: string[]) => ({
  id,
  app_id: id,
  error: { messages: errorMessages },
  job_stage: LifecycleStage.errored,
  progress: { overall_progress: 0 },
});

const createRequestedLifecycle = id => ({
  id,
  app_id: id,
  job_stage: LifecycleStage.requested,
  progress: { overall_progress: 0 },
});

export const actions: IActions = buildEntityActions(
  type,
  selectors,
  api,
  (versionLifecycleActions: IActions) => {
    versionLifecycleActions.getProgress = appId => {
      return EntityActions.loadEntity(
        type,
        async () => {
          const { objects } = await api.loadProgress(appId);
          const latestLifecycleChange = objects[0] || {};

          return { id: appId, ...latestLifecycleChange };
        },
        {},
        appId
      );
    };

    versionLifecycleActions.promote = (
      appId: ID,
      version: string,
      options: PromotionOptions
    ) => {
      return EntityActions.saveEntity(
        type,
        () => {},
        async () => {
          try {
            const result = await api.promote(appId, version, options);

            // We apply some additional fields since we currently call the old
            // promote endpoint which predates transaction manager.
            // When our promote endpoint updates to the transaction manager, we can
            // let its response do the job.
            return {
              ...result,
              ...createRequestedLifecycle(appId),
              to_selected_api: result.selected_api,
              job_kind: LifecycleChangeType.promote,
            };
          } catch (error) {
            const responseText = JSON.parse(error.responseText);
            const errors = responseText?.errors;
            const passes = responseText?.passes;
            const errorMessages =
              Array.isArray(errors) && typeof errors[0] === 'string'
                ? errors
                : errors.total_failures && passes
                ? [
                    'Your integration version doesn’t pass validation. Please validate your integration to see the issues in the validate tab of the right sidebar.',
                  ]
                : [];
            return {
              ...createErroredLifecycle(appId, errorMessages),
              job_kind: LifecycleChangeType.promote,
            };
          }
        },
        [],
        appId
      );
    };

    versionLifecycleActions.migrate = (
      appId: ID,
      fromVersion: string,
      toVersion: string,
      options: MigrationOptions
    ) => {
      return EntityActions.createEntity(
        type,
        () => {},
        async () => {
          try {
            const result = await api.migrate(
              appId,
              fromVersion,
              toVersion,
              options
            );

            return {
              ...result,
              ...createRequestedLifecycle(appId),
              job_kind: LifecycleChangeType.migrate,
            };
          } catch (error) {
            const errors = JSON.parse(error.responseText)?.errors;
            const errorMessages =
              Array.isArray(errors) && typeof errors[0] === 'string'
                ? errors
                : [];
            return {
              ...createErroredLifecycle(appId, errorMessages),
              job_kind: LifecycleChangeType.migrate,
            };
          }
        },
        [],
        appId
      );
    };
  }
);
